(() => {
  'use strict';
  const drawer = document.getElementById('MWSearch');
  if (!drawer || drawer.dataset.searchReady) return;
  drawer.dataset.searchReady = 'true';
  const input = drawer.querySelector('[data-search-input]');
  const form = drawer.querySelector('.mw-search-drawer-form');
  const region = drawer.querySelector('[data-search-results-region]');
  const status = drawer.querySelector('#MWSearchStatus');
  const clear = drawer.querySelector('[data-search-clear]');
  const viewAll = drawer.querySelector('[data-search-view-all]');
  const tabs = [...drawer.querySelectorAll('[data-search-tab]')];
  const defaults = region.innerHTML;
  const config = JSON.parse(document.getElementById('MWConfig')?.textContent || '{}');
  const root = (window.Shopify?.routes?.root || config.root || '/').replace(/\/?$/, '/');
  let active = 'products', timer, controller, sequence = 0, composing = false;
  const cache = new Map();

  function selectTab(name, focus = false) {
    if (!tabs.some(tab => tab.dataset.searchTab === name)) name = 'products';
    active = name;
    tabs.forEach(tab => {
      const selected = tab.dataset.searchTab === name;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focus) tab.focus();
    });
    region.querySelectorAll('[data-search-panel]').forEach(panel => {
      panel.hidden = panel.dataset.searchPanel !== name;
    });
  }

  function reset(message = 'Explore our current collection.') {
    region.innerHTML = defaults;
    region.setAttribute('aria-busy', 'false');
    status.textContent = message;
    viewAll.hidden = true;
    selectTab(active);
  }

  function render(markup, query) {
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const content = parsed.querySelector('[data-search-content]');
    if (!content || !content.querySelector('.mw-search-product')) throw new Error('Missing search content');
    region.replaceChildren(document.importNode(content, true));
    const count = Number(content.dataset.productMatches || 0);
    status.textContent = count > 0
      ? `Products matching “${query}”.`
      : `No products found for “${query}”. You may like these styles.`;
    region.setAttribute('aria-busy', 'false');
    viewAll.hidden = false;
    selectTab(active);
  }

  async function search(query, ticket) {
    try {
      if (cache.has(query)) {
        if (ticket === sequence) render(cache.get(query), query);
        return;
      }
      const current = new AbortController();
      controller = current;
      const timeout = setTimeout(() => current.abort(), 8000);
      let response;
      try {
        const url = new URL(`${root}search/suggest`, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,query,collection,page');
        url.searchParams.set('resources[limit]', '8');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'hide');
        url.searchParams.set('section_id', 'mw-predictive-search');
        response = await fetch(url, {credentials:'same-origin', signal:current.signal});
        if (!response.ok) throw new Error(`Search response ${response.status}`);
        const markup = await response.text();
        if (ticket !== sequence || input.value.trim() !== query || !drawer.open) return;
        render(markup, query);
        if (cache.size >= 20) cache.delete(cache.keys().next().value);
        cache.set(query, markup);
      } finally {
        clearTimeout(timeout);
      }
    } catch (error) {
      if (ticket !== sequence || !drawer.open) return;
      reset('Search is temporarily unavailable. Explore these styles or try again.');
      viewAll.hidden = false;
    }
  }

  function schedule(immediate = false) {
    clearTimeout(timer);
    controller?.abort();
    const ticket = ++sequence;
    const query = input.value.trim().slice(0, 100);
    clear.hidden = !input.value;
    if (!query) {reset(); return;}
    // Keep useful products visible while the next request is in flight.
    region.setAttribute('aria-busy', 'true');
    status.textContent = `Searching for “${query}”…`;
    viewAll.hidden = true;
    timer = setTimeout(() => search(query, ticket), immediate ? 0 : 200);
  }

  input.addEventListener('compositionstart', () => {composing = true;clearTimeout(timer);controller?.abort();sequence++;});
  input.addEventListener('compositionend', () => {composing = false;schedule();});
  input.addEventListener('input', () => {if (!composing) schedule();});
  input.addEventListener('keydown', event => {
    if (event.key !== 'ArrowDown') return;
    const first = region.querySelector(`[data-search-panel="${active}"] :is(a,button)`);
    if (first) {event.preventDefault();first.focus();}
  });
  clear.addEventListener('click', () => {input.value = '';schedule(true);input.focus();});
  drawer.addEventListener('click', event => {
    const suggestion = event.target.closest('[data-search-query]');
    if (suggestion && drawer.contains(suggestion)) {
      input.value = suggestion.dataset.searchQuery;
      selectTab('products');schedule(true);input.focus();
    }
    const tab = event.target.closest('[data-search-tab]');
    if (tab && drawer.contains(tab)) selectTab(tab.dataset.searchTab);
  });
  drawer.querySelector('[role=tablist]').addEventListener('keydown', event => {
    const index = tabs.indexOf(event.target);
    if (index < 0 || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
    event.preventDefault();
    let next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    selectTab(tabs[next].dataset.searchTab, true);
  });
  region.addEventListener('keydown', event => {
    if (event.key === 'ArrowUp' && event.target === region.querySelector(`[data-search-panel="${active}"] :is(a,button)`)) {
      event.preventDefault();input.focus();
    }
  });
  form.addEventListener('submit', event => {
    event.preventDefault();
    const words = input.value.trim().split(/\s+/u).map(word => word.replace(/[\\:*()"\[\]{}]/g, '')).filter(Boolean);
    if (!words.length) {input.focus();return;}
    const query = `(${words.map(word => `title:${word}*`).join(' AND ')}) AND tag:MW-CLOTHING`;
    const url = new URL(form.action, location.origin);
    url.searchParams.set('type', 'product');url.searchParams.set('q', query);
    location.assign(url.href);
  });
  drawer.addEventListener('close', () => {
    clearTimeout(timer);controller?.abort();sequence++;
    if (region.getAttribute('aria-busy') === 'true') reset();
  });
  drawer.addEventListener('cancel', () => {clearTimeout(timer);controller?.abort();sequence++;});
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-open="MWSearch"]')) return;
    // Run after the shared dialog helper's focus callback.
    setTimeout(() => {if (drawer.open) {schedule(true);input.focus();}}, 0);
  });
  selectTab('products');
})();
